import { DeclareMethod } from '/imports/methods';
import type { AdSnapshot } from '/imports/api/adLeads';
import type { OverviewArg } from '/imports/api/overview';

export interface AnalyticsRange {
  /** Inclusive lower bound. Omit for "all time". */
  from?: Date;
  /** Exclusive upper bound. Omit for "now". */
  to?: Date;
}

export interface SeriesPoint {
  /** Bucket start, ISO date (yyyy-mm-dd) or yyyy-mm for month buckets. */
  bucket: string;
  /** Card units the fleet took in (bought). */
  taken: number;
  /** Event tickets the fleet paid out. */
  given: number;
  /** Ticket value of the cards taken, at the price the bot recorded. */
  spend: number;
}

export interface TopCard {
  card: string;
  units: number;
  spend: number;
}

export interface FleetStatus {
  status: string;
  count: number;
}

export interface AcquisitionStats {
  /** Null when the range holds no trade lines at all. */
  first: Date | null;
  last: Date | null;
  lines: number;
  unitsTaken: number;
  unitsGiven: number;
  /** Ticket value of cards taken. */
  spend: number;
  distinctCards: number;
  distinctPartners: number;
  bucket: 'day' | 'month';
  series: SeriesPoint[];
  topCards: TopCard[];
  fleet: FleetStatus[];
  /** Newest trade line in the WHOLE collection, so the UI can say how stale the data is. */
  lastActivityEver: Date | null;
}

/**
 * Buy-side acquisition analytics.
 *
 * Named for what the data actually is. Every `give` line in tradeItems is an
 * Event Ticket at price 1 and every `take` line is a card the fleet bought, so
 * this collection is a purchase ledger, not a sales ledger. There is no
 * sell-side history to compute margin or P&L from, and a dashboard that
 * implied otherwise would be inventing numbers.
 */
export const getAcquisitionStats = DeclareMethod<AnalyticsRange, AcquisitionStats>('getAcquisitionStats');

export interface PricingArg {
  /**
   * Ignore rows whose goatbots buy price is below this, in tix.
   *
   * Not cosmetic. Prices bottom out at 0.001 and the sell floor sits at 0.008,
   * so on bulk the sell/buy ratio is a quantization artifact: the median spread
   * reads 6.0x with no floor and 1.59x on cards worth a ticket or more. Any
   * spread number quoted without a floor is meaningless.
   */
  floor?: number;
  /** Days of history for the series. */
  days?: number;
}

export interface PricingPoint {
  /** Scrape day, yyyy-mm-dd. Days with no scrape are simply absent. */
  bucket: string;
  samples: number;
  /** Median of our buy price over the goatbots buy price. */
  buyRatio: number | null;
  /** Median of our sell price over the goatbots sell price. */
  sellRatio: number | null;
}

export interface PriceGap {
  cardId: number;
  card: string;
  setName: string;
  manBuyPrice: number;
  gbBuyPrice: number;
  /** ours / goatbots. Below 1 means we pay less than they do. */
  ratio: number;
}

export interface PricingStats {
  floor: number;
  latestAt: Date | null;
  cardsPriced: number;
  buyRatio: number | null;
  sellRatio: number | null;
  spread: number | null;
  series: PricingPoint[];
  /** Cards where we pay furthest BELOW goatbots -- the sellers we are losing. */
  underpaying: PriceGap[];
  /** Cards where we pay furthest ABOVE goatbots -- margin we are giving away. */
  overpaying: PriceGap[];
}

/**
 * How our prices compare with goatbots', from priceHistory.
 *
 * The one analytics surface that needs no bot-side instrumentation: priceHistory
 * already carries both sides (man* and gb*) and is current, unlike the trade
 * ledger.
 */
export const getPricingStats = DeclareMethod<PricingArg, PricingStats>('getPricingStats');

export interface FunnelCounts {
  opened: number;
  completed: number;
  /** Closed without completing. */
  abandoned: number;
  /** Neither completed nor closed -- still open, or the bot died holding it. */
  dangling: number;
}

export interface FunnelBucket {
  bucket: string;
  opened: number;
  completed: number;
  abandoned: number;
}

export interface TopPartner {
  partnerId: number;
  partner: string;
  trades: number;
  completed: number;
}

export interface FunnelStats {
  bucket: 'day' | 'month';
  /** Trades with people. */
  customer: FunnelCounts;
  /** Bot-to-bot replenish and deplete, split out so they cannot flatter the rate. */
  internal: FunnelCounts;
  series: FunnelBucket[];
  topPartners: TopPartner[];
  /** How many bot uids were known when classifying. Zero means the split is meaningless. */
  knownBotUids: number;
  lastActivityEver: Date | null;
}

/**
 * Trade funnel from `escrows`.
 *
 * Internal fleet transfers are separated by checking the partner against our own
 * bot uids rather than by a stored flag: nothing marks them, and a completion
 * rate that silently included bot-to-bot replenish would look far healthier than
 * the customer-facing reality.
 */
export const getFunnelStats = DeclareMethod<AnalyticsRange, FunnelStats>('getFunnelStats');

export interface MarginStats {
  /** Sale proceeds minus cost of goods (LIFO basis), in tix. */
  realized: number;
  proceeds: number;
  cogs: number;
  unitsSold: number;
  salesLines: number;
  /** Units acquired but not yet sold, and what they cost. */
  openUnits: number;
  openCost: number;
  /**
   * Units sold with no acquisition lot to match against. Non-zero means the
   * ledger is missing history, so `realized` understates cost and overstates
   * margin. Surfaced rather than hidden.
   */
  unmatchedUnits: number;
  /** Rows skipped because they are priced in USD and cannot be mixed with tix. */
  skippedCashLines: number;
  topCards: { card: string; unitsSold: number; realized: number }[];
  lastActivityEver: Date | null;
}

/**
 * Realized margin over the trade ledger, on a LIFO cost basis.
 *
 * LIFO charges a sale against the most recently bought copy, which tracks
 * replacement cost -- the right frame for a fleet that rebuys constantly at
 * moving prices, and what the mogbots-ops reference documents. The basis is a
 * parameter in imports/api/costBasis.ts, so switching is one argument.
 *
 * Computed here rather than maintained as a running ledger in the bot: given
 * time-ordered lines, cost basis is a pure function of history, so the money
 * path needs no new state.
 *
 * Event Tickets are excluded from the walk. They are the currency, not stock --
 * treating them as inventory would book every payment as a trade.
 */
export const getMarginStats = DeclareMethod<AnalyticsRange, MarginStats>('getMarginStats');

export interface InventoryPoint {
  bucket: string;
  skus: number;
  units: number;
  tradeable: number;
  valueAtBuy: number;
  valueAtSell: number;
  bots: number;
}

export interface InventoryStats {
  series: InventoryPoint[];
  latest: InventoryPoint | null;
  perBot: { bot: string; units: number; valueAtBuy: number }[];
  /** Snapshots only start accruing when the cron first runs; say so if empty. */
  snapshotCount: number;
}

/** Inventory over time, from the snapshot roll-up rather than the live `assets`. */
export const getInventoryStats = DeclareMethod<{ days?: number }, InventoryStats>('getInventoryStats');

/** Our store credit at external bot chains, as the chains last stated it in
 *  chat (chainCredits): per chain family across all our bots, and the total. */
export interface ChainCreditChain { family: string; credit: number; bots: number; latestAt: Date }
export interface ChainCreditStats {
  total: number;
  chains: ChainCreditChain[];
  at: Date;
}
export const getChainCreditStats = DeclareMethod<void, ChainCreditStats>('getChainCreditStats');

/**
 * Held stock whose cost sits above what the market would pay for it today.
 *
 * COST is FIFO: sales are charged against the oldest copies, so what is left on
 * the shelf is what we bought most recently. That is replacement cost, the
 * figure a sale actually has to beat. LIFO would charge sales against the newest
 * copies and leave the oldest and cheapest standing, which flatters the shelf.
 * Measured over the whole fleet on 2026-09-24 the two differed by ten tickets
 * out of seven hundred, so the choice is about honesty, not magnitude.
 *
 * MARKET is the Goatbots bid. It is the one exit that is always open, it clears
 * in size, and someone other than us sets it. Our own ask is carried for
 * context and is deliberately not the yardstick: marking a card up would erase
 * a loss on paper without selling anything.
 */
export interface DeficitCard {
  cardId: number;
  card: string;
  /** Copies the fleet holds right now, from `assets`, not from the walk. */
  units: number;
  /** What one held copy cost on a FIFO basis. The deficit is measured on this. */
  unitCost: number;
  /** Every copy we ever bought, averaged, held or not. Shown alongside the FIFO
   *  figure because the two diverge exactly where a card has mixed lots, which
   *  is the case worth looking at by hand. Null when nothing priced it. */
  avgCost: number | null;
  /** Goatbots' bid: what they would pay us today. The deficit's yardstick. */
  marketBid: number;
  /** Goatbots' own ask, for the spread the card trades in. */
  marketAsk: number | null;
  /** What we charge for it. Context only, never the yardstick. */
  ourAsk: number | null;
  /** unitCost - marketBid, for ONE copy. Positive means a paper loss.
   *  Per copy rather than per position on purpose: the question this panel
   *  answers is "how far is this card from being sellable", which is a property
   *  of the card, not of how many we happen to be sitting on. The position-wide
   *  figure is DeficitStats.deficit. */
  deficit: number;
}

export interface DeficitStats {
  /** The worst offenders, largest deficit first, capped by `limit`. */
  cards: DeficitCard[];
  /** Totals over EVERY underwater card, not just the ones listed. */
  underwaterCards: number;
  units: number;
  /** The money: every underwater copy's gap summed, so units ARE weighted here.
   *  Deliberately a different shape from the per-copy DeficitCard.deficit, so
   *  the column does not sum to this and is not meant to. */
  deficit: number;
  /** Held cards this basis could put a positive cost on. The rest are bulk the
   *  fleet took for nothing, or stock with no acquisition in the ledger. Either
   *  way they are unknown rather than underwater: a card that cost us nothing
   *  cannot be carrying a loss, and a missing cost is not evidence of one. */
  pricedCards: number;
  costlessCards: number;
  /** Held cards with no Goatbots bid, so nothing to measure against. */
  unquotedCards: number;
  at: Date;
}

export const getUnderwaterStock = DeclareMethod<{ limit?: number }, DeficitStats>('getUnderwaterStock');

export interface BotReliability {
  bot: string;
  crashes: number;
  restarts: number;
  stalls: number;
  transitions: number;
}

export interface ReliabilityStats {
  windowDays: number;
  crashes: number;
  restarts: number;
  stalls: number;
  transitions: number;
  perBot: BotReliability[];
  eventCount: number;
}

/**
 * Fleet reliability from the status-event log.
 *
 * Counts transitions rather than reconstructing time-in-state. Interval maths
 * needs the state at the window boundary and an unbroken event stream, and this
 * log has neither -- it only captures while the Meteor process is up. Counting
 * transitions into crashed/restarting/stalled is robust to gaps and answers the
 * question that actually matters: which bots keep falling over.
 */
export const getReliabilityStats = DeclareMethod<{ days?: number }, ReliabilityStats>('getReliabilityStats');

export interface BotPnl {
  bot: string;
  botId: number;
  /** Units and tix the bot put OUT to acquire stock. */
  unitsAcquired: number;
  spend: number;
  /** Units and tix the bot took IN from sales. */
  unitsSold: number;
  proceeds: number;
  /**
   * proceeds - spend. Unambiguous and needs no cost model, but it is a CASH
   * FLOW, not a profit: buying stock makes it negative even when the trade was
   * good, because the stock acquired is not counted.
   */
  netFlow: number;
  /** Cost-basis margin for this bot's own lots. See unmatchedUnits first. */
  realized: number;
  /**
   * Units this bot sold with no lot of its own. Almost always means the cards
   * arrived from a DEPOT (a card replenish), which is a friendly trade and so
   * is not written to the ledger -- leaving `realized` overstated by exactly
   * those units' true cost.
   */
  unmatchedUnits: number;
  trades: number;
}

export interface PerBotStats {
  bots: BotPnl[];
  /** Total unmatched across the fleet, i.e. how much per-bot margin to distrust. */
  totalUnmatched: number;
  lastActivityEver: Date | null;
}

/**
 * Per-bot flows and cost-basis margin.
 *
 * Cost basis is computed per bot, which is what makes it per-bot -- and also
 * what makes it approximate, because DEPOT transfers move cards between bots
 * with no ledger line (fileInvoice skips friendly trades). Ticket movement to
 * and from a banker is irrelevant here: tickets are excluded from the walk.
 *
 * How much this bites depends on configuration. defaultConfig has
 * `replenish: true` and `depleteCards: true`, so card transfers are on by
 * default; a bot set to `hoard` keeps its stock and its per-bot figures stay
 * self-contained.
 *
 * Both figures are returned so the honest one (netFlow) sits next to the useful
 * one (realized).
 */
export const getPerBotStats = DeclareMethod<AnalyticsRange, PerBotStats>('getPerBotStats');

export interface DeadStockRow {
  cardId: number;
  card: string;
  units: number;
  bots: number;
  /**
   * Most recent ledger line of any kind for this card, null if there is none.
   *
   * Null does NOT prove the card has never traded. Cards also move between bots
   * via a depot, and those are friendly trades that fileInvoice never records
   * while still updating `assets` -- so stock can arrive with no ledger line at
   * all. Read it as "no customer-facing activity", not "never touched".
   */
  lastMoved: Date | null;
  daysIdle: number | null;
  /** No `give` line has ever been recorded for this card: bought, never sold. */
  neverSold: boolean;
  /** What it would fetch at our current ask, in tix. */
  valueAtSell: number;
}

export interface DeadStockStats {
  thresholdDays: number;
  rows: DeadStockRow[];
  totalUnits: number;
  totalValue: number;
  /** Everything currently held, for context on how much of it is dead. */
  heldUnits: number;
  heldValue: number;
}

/**
 * Stock that is not moving.
 *
 * Joins live `assets` to the last ledger movement per SKU. Capital sitting in
 * cards nobody buys is the most actionable thing in the inventory, and neither
 * collection can show it alone: `assets` knows what is held but not for how
 * long, and the ledger knows movement but not what remains.
 */
export const getDeadStock = DeclareMethod<{ days?: number }, DeadStockStats>('getDeadStock');

export interface TraderRow {
  partnerId: number;
  partner: string;
  trades: number;
  completed: number;
  firstSeen: Date | null;
  lastSeen: Date | null;
}

export interface TraderPoint {
  bucket: string;
  /** Traders whose FIRST ever trade fell in this bucket. */
  newTraders: number;
  /** Traders active in this bucket who had traded before it. */
  returning: number;
}

export interface TraderStats {
  /** Share of traders whose FIRST ever trade completed. Null when unknown. */
  firstTradeCompletion: number | null;
  /** Completion rate across every trade after a trader's first. */
  repeatTradeCompletion: number | null;
  /** Distinct people traded with in the range. */
  uniqueTraders: number;
  newTraders: number;
  returningTraders: number;
  /** Share of traders in the range with more than one trade, 0-1. */
  repeatRate: number;
  leaderboard: TraderRow[];
  cohorts: { cohort: string; size: number; retention: number[] }[];
  series: TraderPoint[];
  /** Zero means internal transfers could not be excluded and counts are inflated. */
  knownBotUids: number;
  lastActivityEver: Date | null;
}

/**
 * Who trades with us, from `escrows`.
 *
 * The one analytics surface with deep real history: escrows reach back to 2018
 * and carry partner identity, so new-vs-returning, repeat rate and cohort
 * retention are all answerable with no instrumentation at all. First-seen is
 * computed over ALL history regardless of the selected range -- a trader is
 * only "new" if they had never traded before, not merely absent from the window.
 */
export const getTraderStats = DeclareMethod<AnalyticsRange, TraderStats>('getTraderStats');

export interface CardPricePoint {
  at: Date;
  manBuyPrice: number | null;
  manSellPrice: number | null;
  gbBuyPrice: number | null;
  gbSellPrice: number | null;
}

export interface CardPriceHistory {
  cardId: number;
  card: string;
  setName: string;
  points: CardPricePoint[];
  /** Most recent ratios, for the header. Null when either side is unpriced. */
  buyRatio: number | null;
  sellRatio: number | null;
}

/**
 * One card's price against goatbots' over time.
 *
 * Rides the existing `{cardId, at: -1}` index on priceHistory, so this is a
 * seek rather than a scan even though the collection holds ~1M points.
 */
export const getCardPriceHistory = DeclareMethod<{ cardId: number; days?: number }, CardPriceHistory>('getCardPriceHistory');

export type TradeIntent = 'seller' | 'buyer' | 'hybrid' | 'unknown';
export type TradeLogSort = 'recent' | 'tix' | 'binder' | 'opens';

export interface TradeSessionRow {
  _id: string;
  at: Date;
  bot: string;
  botId: number;
  /** The bot document's id, for the link; null when the bot is gone. */
  botDocId: string | null;
  partner: string;
  partnerId: number;
  mode: string;
  kind: string;
  binderCount: number;
  wantedCount: number;
  skippedCount: number;
  takenCount: number;
  addedCardsToBuy: boolean;
  botMadeOffer: boolean;
  botWantedNothing: boolean;
  reachedConfirm: boolean;
  completed: boolean;
  ticketShortfall: boolean;
  shortfallResolved: boolean;
  direction: string;
  durationMs: number;
  tradeno: number | null;
  reason: string | null;
  /** Resolved on read by joining `cards`; the row itself stores ids only. */
  wanted: { cardId: number; card: string; qty: number }[];
  /** We opened the trade (an eager or manual invitation). */
  initiatedByUs: boolean;
  /** What the filed trade moved; zero on a trade that never completed. */
  tixIn: number;
  tixOut: number;
  cardsIn: number;
  cardsOut: number;
  /** Completed, tickets paid out, no cards either way: they cashed out credit. */
  creditPayout: boolean;
  partnerIsBot: number | null;
  /** From the partner's whole history with us, not this trade. */
  intent: TradeIntent;
  partnerOpens: number;
}

export interface TradeLogStats {
  rows: TradeSessionRow[];
  /** Counts per outcome across the whole filtered set, not just the page. */
  byKind: { kind: string; count: number }[];
  total: number;
  /** Rows matching every filter including kind; `rows` is the first page of them. */
  matched: number;
  uniqueTraders: number;
  tixMoved: number;
  /** Trades taken while the bot was short of tickets. */
  shortfallCount: number;
  /** Zero means nothing has been recorded yet -- the fleet has not traded. */
  collectionSize: number;
}

export interface TradeLogArg extends AnalyticsRange {
  kind?: string;
  /** Page size, 1..500. */
  limit?: number;
  /** Partner or bot name, case-insensitive substring. */
  q?: string;
  intent?: TradeIntent;
  sort?: TradeLogSort;
  /** true: only trades we opened; false: only inbound; omit: both. */
  initiatedByUs?: boolean;
  partnerId?: number;
}

/**
 * The trade log, from `tradeSessions`.
 *
 * Only customer trades are recorded, and only from the point the bot accepted:
 * invitations declined for blacklist, census or busy never produce a row. So
 * "opened" here means "we took it on", which is the population worth studying.
 */
export const getTradeLog = DeclareMethod<TradeLogArg, TradeLogStats>('getTradeLog');

export interface TradeCardLine {
  cardId: number;
  card: string;
  set: string;
  rarity: string;
  foil: boolean;
  qty: number;
  /** Unit price: what was filed on a completed trade, else our book price today. */
  price: number | null;
  /** True when `price` is today's book price rather than what was filed. */
  bookPrice: boolean;
  status: 'bought' | 'wanted' | 'not bought';
}

export interface TradeCounterparty {
  partnerId: number;
  partner: string;
  intent: TradeIntent;
  /** Sessions with us, since sessions began. */
  opens: number;
  completes: number;
  firstSeen: Date | null;
  lastSeen: Date | null;
  /** Their first trade ever, from the escrow history that predates sessions. */
  firstTrade: Date | null;
  /** Realized profit on every card we ever sold them, at held cost. */
  profit: number;
  profitUnits: number;
  /** First trade within the last 30 days. */
  isNew: boolean;
}

/** Realized profit on cards sold, at the held cost the money rule floors on. */
export interface TradeProfitHeld {
  value: number;
  proceeds: number;
  cost: number;
  units: number;
  /** Copies costed from the buy book rather than a held lot. */
  estimatedUnits: number;
  /** Copies with no cost at all, counted at zero. */
  unknownUnits: number;
}

export interface TradeInspection {
  row: TradeSessionRow;
  escrowId: string | null;
  /** The bot document's id, for the link; null when the bot is gone. */
  botDocId: string | null;
  summary: string;
  weTook: TradeCardLine[];
  theyTook: TradeCardLine[];
  stages: { label: string; done: boolean; at: Date | null }[];
  counterparty: TradeCounterparty;
  /** This trade's sales at held cost. Null when nothing was sold. */
  profit: TradeProfitHeld | null;
  ad: AdSnapshot | null;
  partnerBotSignals: string[];
  creditScope: string | null;
}

/** One trade in full: the session, its ledger lines priced, and the partner's standing with us. */
export const getTradeInspection = DeclareMethod<{ id: string }, TradeInspection | null>('getTradeInspection');

export interface TradeProfitLifo {
  proceeds: number;
  cogs: number;
  profit: number;
  /** Sold copies no lot backed; cogs is understated by their cost. */
  unmatchedUnits: number;
  /** Ledger lines walked to reach this trade. */
  linesWalked: number;
}

/** The exact LIFO cost of one trade's sales: a walk of the whole ledger up to
 *  that trade. On demand only; the inspection's default is the held cost. */
export const getTradeProfitLifo = DeclareMethod<{ id: string }, TradeProfitLifo | null>('getTradeProfitLifo');

/** One partner's whole timeline with us, newest first. */
export const getCounterpartyTrades = DeclareMethod<{ partnerId: number; limit?: number }, { rows: TradeSessionRow[]; total: number }>('getCounterpartyTrades');

export interface SaturatedTarget {
  cardId: number;
  card: string;
  buycap: number;
  priority: number;
  /** Buying bots already holding buycap or more, which will refuse the card. */
  botsAtCap: number;
  botsHolding: number;
  totalHeld: number;
  /** Copies the fleet could still take before every buying bot is full. */
  headroom: number;
}

export interface UnderwaterCard {
  cardId: number;
  card: string;
  units: number;
  /** Quantity-weighted average of what we actually paid, from the ledger. */
  avgCost: number;
  /** What we currently ask for it. */
  sellPrice: number;
  /** units * (avgCost - sellPrice): the paper loss at our own ask. */
  loss: number;
}

export interface InventoryHealth {
  targets: number;
  /** Targets where NO buying bot has room left. */
  saturatedTargets: number;
  saturated: SaturatedTarget[];
  underwater: UnderwaterCard[];
  underwaterUnits: number;
  underwaterLoss: number;
  /** Buying bots found. Zero means saturation cannot be judged at all. */
  buyingBots: number;
  /** How many ledger lines carry a usable cost, i.e. how much of this is knowable. */
  pricedLines: number;
}

/**
 * Where the buying is jammed, and where it lost money.
 *
 * Two questions no existing screen answers. `buycap` caps TOTAL ownership per
 * bot -- buybot subtracts what the bot already holds (buybot.ts:83-85) -- so a
 * target whose cap is met is silently refused rather than recorded as a miss.
 * And a card bought above what we now ask is a loss that dead stock cannot
 * catch, because such a card may be turning over perfectly well.
 */
export const getInventoryHealth = DeclareMethod<void, InventoryHealth>('getInventoryHealth');

export interface BotHealthRow {
  bot: string;
  botId: number;
  mode: string;
  status: string;
  active: boolean;
  /** Transitions in the window. Zero can mean healthy OR never started. */
  crashes: number;
  stalls: number;
  restarts: number;
  /** From the latest inventory snapshot; null when none has been taken. */
  units: number | null;
  valueAtBuy: number | null;
  /** Lifetime ledger flows for this bot. */
  trades: number;
  unitsAcquired: number;
  spend: number;
}

export interface BotHealthStats {
  windowDays: number;
  rows: BotHealthRow[];
  byStatus: { status: string; count: number }[];
  /** Whether the supporting logs hold anything yet, so empty columns can say why. */
  hasStatusEvents: boolean;
  hasSnapshots: boolean;
}

/**
 * One row per bot, joining the three things that describe its health.
 *
 * Joined server-side rather than in the component because the per-bot figures
 * elsewhere are top-N slices: a client-side join would silently leave the
 * quieter bots with blank columns and no indication they had been truncated.
 *
 * Deliberately no uptime percentage. The status log only records while the
 * Meteor process is up, so time-in-state cannot be reconstructed honestly;
 * counting transitions is robust to those gaps and answers the question that
 * actually matters -- which bots keep falling over.
 */
export const getBotHealth = DeclareMethod<{ days?: number }, BotHealthStats>('getBotHealth');

/** One advertised card, ranked by the leads and conversions it appeared in. */
export interface AdCardStat {
  cardId: number;
  card: string;
  set: string;
  /** Invitations received while this card was visible in the classified. */
  leads: number;
  /** Of those, how many reached a completed trade. */
  conversions: number;
  /** conversions / leads. */
  rate: number;
  /** Leads the bot turned away (busy/census). A subset of `leads`. */
  declined: number;
}

export interface AdAttributionStats {
  rows: AdCardStat[];
  totals: { leads: number; conversions: number; rate: number; cards: number };
  /**
   * Leads whose recorded ad was older than one rotation when the invitation
   * arrived, so the customer likely saw a different classified. Surfaced rather
   * than silently included, because it bounds how much of this is guesswork.
   */
  staleLeads: number;
  /** Leads in range at all, including those with no ad attached. */
  totalLeads: number;
}

/**
 * Ranked ad attribution.
 *
 * IMPORTANT FOR INTERPRETATION: a "lead" is a trade invitation that arrived
 * while the card was being advertised. We cannot observe whether the customer
 * read the classified, so this is correlation, not causation -- a repeat
 * customer produces the same row as someone who clicked the ad. Read it as
 * "trades opened while X was advertised", not "trades caused by advertising X".
 */
export const getAdAttribution = DeclareMethod<AnalyticsRange & { limit?: number }, AdAttributionStats>('getAdAttribution');

/** Fleet-wide inventory value as a range: every held copy (tickets aside)
 *  summed at the money rule's floor -- lot cost or ask, whichever is lower,
 *  but never below Goatbots' bid -- and at our ask. Live asset rows, not the
 *  nightly snapshot. */
export interface InventoryValue {
  at: Date;
  /** Distinct (bot, card, type) rows and copies held. */
  skus: number;
  units: number;
  bots: number;
  floorValue: number;
  askValue: number;
}
export const getInventoryValue = DeclareMethod<Record<string, never>, InventoryValue>('getInventoryValue');

// ---------------------------------------------------------------- overview ----

export type { OverviewArg } from '/imports/api/overview';

export interface OverviewWindow {
  /** Customer trades opened and completed (trade sessions). */
  opens: number;
  completes: number;
  /** Card copies and tickets each way, from the ledger. */
  cardsBought: number;
  cardsSold: number;
  tixIn: number;
  tixOut: number;
  /** Held-cost profit on the cards sold. Null when nothing was sold. */
  profit: TradeProfitHeld | null;
}

export interface OverviewStats {
  isToday: boolean;
  hasPrior: boolean;
  from: Date | null;
  to: Date;
  current: OverviewWindow;
  /** The comparison window's figures; null for all time. */
  prior: OverviewWindow | null;
  /** Share of the local day elapsed (Today), for the pace projection. */
  dayFraction: number;
  /** When trade sessions begin; the funnel is blind before it. */
  sessionsSince: Date | null;
  /** Today's opens by local hour. */
  hourly: number[];
  todayOpens: number;
  at: Date;
}

/** The Dashboard overview for one window, memoized for a minute. */
export const getOverviewStats = DeclareMethod<OverviewArg, OverviewStats>('getOverviewStats');

/** Exact LIFO profit for the window: a walk of the whole ledger. On demand. */
export const getOverviewProfitLifo = DeclareMethod<OverviewArg, TradeProfitLifo>('getOverviewProfitLifo');

export interface ProfitByCardRow {
  cardId: number;
  card: string;
  set: string;
  foil: boolean;
  trades: number;
  units: number;
  proceeds: number;
  cost: number;
  profit: number;
  estimatedUnits: number;
  unknownUnits: number;
}

/** Held-cost profit per card sold in the window, best first. */
export const getProfitByCard = DeclareMethod<OverviewArg, { rows: ProfitByCardRow[] }>('getProfitByCard');

export interface CardLineRow {
  _id: string;
  at: Date | null;
  cardId: number;
  card: string;
  set: string;
  foil: boolean;
  qty: number;
  price: number | null;
  currency: string;
  partnerId: number;
  partner: string;
  botId: number;
  bot: string;
  botDocId: string | null;
  /** The trade session this line belongs to, for the inspector link. */
  sessionId: string | null;
}

/** The ledger's card lines for one side of the window, newest first. */
export const getCardLines = DeclareMethod<OverviewArg & { side: 'buy' | 'sell'; limit?: number }, { rows: CardLineRow[]; total: number }>('getCardLines');
